import type { ReactNode } from "react";

// L52 — Sofía visual-craft pass. Page-shell header — brand mark + a
// page-specific eyebrow label + display title + subtitle + optional actions.
// Rendered by the sub-page shells: HireAgent (`/hire`) and ApprovalDetail
// (`/approvals/:id`). The dashboard `/` and agent pages use their own hero
// headers (`HeroMaxiWeek` / `HeroEditorial` / `HeroAgentWeek`), not this.
// The signature ribbon is fixed at body::before and is not part of this
// component — see styles.css :root + body::before.
//
// NOTE: `eyebrow` here is a page label ("Contrato del agente", "Propuesta
// para aprobar") — NOT the "Agentes Nube" brand wordmark. The build-version
// chip (<AppVersion/>) belongs with that wordmark, so it lives in the hero
// eyebrows, not here.
//
// L167 — Clara: the eyebrow brand mark is the TiendaNube logo, not the
// custom 3-dot orbit glyph. agentes-nube ships through TiendaNube's Partner
// App Store and renders inside TN's chrome via Nexo, so the first mark a
// merchant sees in every page-shell header is TiendaNube's. See <TiendaNubeLogo>.
export function BrandHeader({
  eyebrow,
  title,
  subtitle,
  actions
}: {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="brandHeader">
      <div className="brandHeader__main">
        <p className="brandHeader__eyebrow">
          <TiendaNubeLogo />
          <span>{eyebrow}</span>
        </p>
        <h1 className="brandHeader__title">{title}</h1>
        {subtitle ? <p className="brandHeader__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="brandHeader__actions">{actions}</div> : null}
    </header>
  );
}

// TiendaNube brand mark — the official TiendaNube logo (two interlocking
// rings). The path mirrors `src/assets/tiendanube.svg`, the committed
// brand-asset source-of-truth; it is inlined here — like the legacy BrandMark
// it replaces — so the mark needs no per-render asset request inside the Nexo
// iframe and tints from a single lawful Nimbus token (`--color-primary`, set
// on `.tiendanubeLogo` in styles.css). A monochrome mark in one approved
// brand color is the safest treatment under TN's partner-app brand guidance.
// Decorative: every eyebrow already carries the "Agentes Nube" wordmark in
// text, so the mark is aria-hidden.
export function TiendaNubeLogo({ size = 18 }: { size?: number }) {
  return (
    <svg
      className="tiendanubeLogo"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      data-testid="tiendanube-logo"
      aria-hidden="true"
    >
      <path d="M10.25 2.24a5.79 5.79 0 0 0-4 1.63 4.48 4.48 0 1 0 0 8.26 5.76 5.76 0 1 0 4-9.89zm0 10.24A4.49 4.49 0 0 1 5.76 8H4.48a5.74 5.74 0 0 0 .89 3.07 3.29 3.29 0 0 1-.88.13 3.2 3.2 0 0 1 0-6.4A3.2 3.2 0 0 1 7.69 8H9a4.42 4.42 0 0 0-1.63-3.43 4.48 4.48 0 1 1 2.88 7.91z" />
    </svg>
  );
}

// Build-version chip — the single shared renderer for the `git describe`
// version (`__APP_VERSION__`, injected by vite.config `define`). Sits next to
// the "Agentes Nube" wordmark in every hero eyebrow (HeroMaxiWeek /
// HeroEditorial / the DashboardSkeleton + full-page-state eyebrows) so a
// deploy is version-verifiable at a glance: the release tag on a released
// build, `<tag>-<n>-g<sha>` / `-dirty` otherwise. One component + one
// `.appVersion` rule — never duplicate the markup per surface.
export function AppVersion() {
  return (
    <span className="appVersion" data-testid="brand-version">
      {`v${__APP_VERSION__}`}
    </span>
  );
}
